import React from 'react';
import { cn } from '@express-labs/raven-tools';
import {
  CarouselProvider,
  Slider,
  ButtonBack,
  ButtonNext,
} from 'pure-react-carousel';
import { SLIDE_HEIGHT, SLIDE_WIDTH } from '../../shared';
import IconLeft from '../IconLeft';
import IconRight from '../IconRight';
import s from './CarouselWithHtml.scss';
import RenderSlideHtml from '../RenderSlideHtml';

export type CarouselWithHtmlProps = {
  cards?: never;
  /**  Moves the nth slide into the leftmost position of the carousel */
  currentSlide?: number;
  deviceWidth: 'desktop' | 'mobile';
  /** array of html nodes */
  mainImages?: React.ReactElement[];
  peekSize?: number;
  /** height of the html nodes */
  slideHeight?: number;
  /** width of the html nodes */
  slideWidth?: number;
  visibleSlides: number;
};

type PropsInjectedByCarousel = {
  /**  a unique id for the carousel. This number is used to help create unique css id's and class
  names so that you can have more than one carousel on the page and style them differently,
  or reference a dom element via their unique id attribute name via javascript.
  This number is generated automatically in Carousel and passed in. */
  uid: number;
};

const CarouselWithHtml = ({
  currentSlide = 0,
  deviceWidth,
  mainImages = [],
  slideWidth = SLIDE_WIDTH,
  slideHeight = SLIDE_HEIGHT,
  visibleSlides,
  peekSize = 0.2,
  uid,
}: CarouselWithHtmlProps & PropsInjectedByCarousel) => {
  const isDesktopDevice = deviceWidth === 'desktop';
  const totalSlides = mainImages.length;
  const isLimitSlidesExceeded = totalSlides > visibleSlides;
  const customVisibleSlides = isLimitSlidesExceeded
    ? visibleSlides + peekSize
    : totalSlides;

  return (
    <CarouselProvider
      className={cn(s.container, 'carousel--with-html')}
      currentSlide={currentSlide}
      data-testid="CarouselWithHtml"
      data-uid={uid}
      lockOnWindowScroll
      naturalSlideHeight={slideHeight}
      naturalSlideWidth={slideWidth}
      totalSlides={totalSlides}
      visibleSlides={customVisibleSlides}
    >
      <div className={s.sliderButtonContainer}>
        <Slider>
          {mainImages.map((item, i) => {
            return (
              <RenderSlideHtml
                key={item?.key}
                htmlItem={item}
                i={i}
                uid={uid}
              />
            );
          })}
        </Slider>
        {isLimitSlidesExceeded && isDesktopDevice && (
          <>
            <ButtonBack className={s.back}>
              <IconLeft />
            </ButtonBack>
            <ButtonNext className={s.next}>
              <IconRight />
            </ButtonNext>
          </>
        )}
      </div>
    </CarouselProvider>
  );
};

export default CarouselWithHtml;
